import {
    autoSignIn,
    confirmUserAttribute,
    confirmSignUp,
    confirmResetPassword,
    getCurrentUser,
    fetchUserAttributes,
    resetPassword,
    sendUserAttributeVerificationCode,
    signIn,
    signInWithRedirect,
    signOut,
    signUp,
    updateUserAttributes,
} from "aws-amplify/auth";

const EMAIL_ATTRIBUTE_KEY = "email";
const GOOGLE_LINKED_ATTRIBUTE_KEY = "custom:has_google_linked";
const GOOGLE_ONBOARDING_ATTRIBUTE_KEY = "custom:has_onboarded";
const NATIVE_PASSWORD_ATTRIBUTE_KEY = "custom:has_native_password";
const GOOGLE_AUTH_INTENT_KEY = "google_auth_intent";

function isGoogleFederatedUsername(username) {
    return typeof username === "string" && username.toLowerCase().startsWith("google_");
}

export function validatePreferredUsername(value) {
    if (typeof value !== "string") {
        return "Display name is required";
    }

    if (!value.trim()) {
        return "Display name is required";
    }

    if (!value.match(/^[a-z0-9._]+$/i)) {
        return "Display name cannot contain special characters or spaces";
    }

    if (value.length > 50) {
        return "Display name cannot be longer than 50 characters";
    }

    return null;
}

function normalizeMailingListValue(value) {
    return value === true || value === "true" ? "1" : "0";
}

function normalizeEmail(value) {
    return typeof value === "string" ? value.trim() : "";
}

function normalizeGoogleAuthErrorMessage(value) {
    if (typeof value !== "string") {
        return null;
    }

    const trimmedValue = value.trim();
    if (!trimmedValue) {
        return null;
    }

    if (trimmedValue.toLowerCase().includes("user already exists")) {
        return "This Google account is already registered. Please continue with Google sign in.";
    }

    return trimmedValue;
}

export async function signInUser({email, password}) {
    return signIn({
        username: email,
        password,
    });
}

export async function signUpUser({
    email,
    password,
    preferredUsername,
    mailingListOptIn,
}) {
    const normalizedEmail = normalizeEmail(email);

    return signUp({
        username: normalizedEmail,
        password,
        options: {
            userAttributes: {
                email: normalizedEmail,
                preferred_username: preferredUsername,
                "custom:OptIntoMailingList": normalizeMailingListValue(mailingListOptIn),
            },
            autoSignIn: true,
        },
    });
}

export async function completeAutoSignIn() {
    return autoSignIn();
}

export async function sendPasswordResetCode({email}) {
    return resetPassword({username: email});
}

export async function submitPasswordReset({email, confirmationCode, password}) {
    return confirmResetPassword({
        username: email,
        confirmationCode,
        newPassword: password,
    });
}

export async function confirmUserSignUp({username, code}) {
    return confirmSignUp({
        username,
        confirmationCode: code,
    });
}

export async function sendCurrentUserEmailVerificationCode() {
    return sendUserAttributeVerificationCode({
        userAttributeKey: EMAIL_ATTRIBUTE_KEY,
    });
}

export async function confirmCurrentUserEmailVerificationCode({confirmationCode}) {
    return confirmUserAttribute({
        userAttributeKey: EMAIL_ATTRIBUTE_KEY,
        confirmationCode,
    });
}

export async function fetchCurrentUser() {
    return getCurrentUser();
}

export async function signOutCurrentSession() {
    return signOut({ global: false });
}

export async function signInWithGoogle(customState) {
    localStorage.setItem(GOOGLE_AUTH_INTENT_KEY, customState);
    return signInWithRedirect({ provider: "Google", customState });
}

export async function linkGoogleAccount() {
    localStorage.setItem(GOOGLE_AUTH_INTENT_KEY, "link");
    // Must sign out locally first — signInWithRedirect throws if a user is
    // already authenticated. global: false avoids the Cognito logout redirect.
    await signOut({ global: false });
    return signInWithRedirect({ provider: "Google", customState: "link" });
}

// Used after the Google Hosted UI link handoff needs a retry.
// At this point the session is already cleared by the OAuth error, so no signOut needed.
export async function retryLinkGoogleAccount() {
    localStorage.setItem(GOOGLE_AUTH_INTENT_KEY, "link");
    return signInWithRedirect({ provider: "Google", customState: "link" });
}

export async function fetchCurrentUserAttributes() {
    return fetchUserAttributes();
}

export async function fetchUserIdentities() {
    const [attributes, currentUser] = await Promise.all([fetchUserAttributes(), getCurrentUser()]);
    let identities = [];
    try {
        identities = JSON.parse(attributes.identities ?? "[]");
    } catch {
        identities = [];
    }
    // Google-only accounts use a google_ federated username prefix.
    // Email/password accounts that have linked Google are flagged via the
    // custom:has_google_linked attribute set during the Cognito linking flow.
    // The identities attribute is checked as an additional fallback.
    const isGoogleOnlyAccount = isGoogleFederatedUsername(currentUser.username);
    const hasGoogle =
        isGoogleOnlyAccount ||
        identities.some((i) => i.providerName === "Google") ||
        attributes[GOOGLE_LINKED_ATTRIBUTE_KEY] === "1";
    const sub = attributes.sub ?? null;
    // custom:has_native_password is legacy metadata for accounts that already
    // have a native Cognito password associated with them.
    const hasSetPassword = !isGoogleOnlyAccount || attributes[NATIVE_PASSWORD_ATTRIBUTE_KEY] === "1";
    const hasCompletedOnboarding = attributes[GOOGLE_ONBOARDING_ATTRIBUTE_KEY] === "1";
    return { hasGoogle, sub, hasSetPassword, isGoogleOnlyAccount, hasCompletedOnboarding };
}

export function getGoogleAuthFailureMessage(payload) {
    const message = normalizeGoogleAuthErrorMessage(payload?.data?.message ?? payload?.message);
    if (message) {
        return message;
    }

    const errorDescription = normalizeGoogleAuthErrorMessage(
        payload?.data?.error_description ?? payload?.data?.error
    );
    if (errorDescription) {
        return errorDescription;
    }

    return "Google sign-in failed. Please try again.";
}

export async function updateGoogleUserAttributes({ preferredUsername, mailingListOptIn }) {
    return updateUserAttributes({
        userAttributes: {
            preferred_username: preferredUsername,
            "custom:OptIntoMailingList": normalizeMailingListValue(mailingListOptIn),
            [GOOGLE_ONBOARDING_ATTRIBUTE_KEY]: "1",
        },
    });
}
